import React, { useState, useEffect, useRef } from 'react';
import { X, User, PhoneCall, Mail, MessageSquare, Mic, Upload, ClipboardCheck, Calendar, Percent, FileText, Search, MapPin } from 'lucide-react';
import './ActionOperations.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Alert from '../../../common/Alerts/Alert';

const ActionOperations = ({ task, onBack, leadId: propLeadId }) => {
    // Shared State
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userList, setUserList] = useState([]);
    const [salesActivityOptions, setSalesActivityOptions] = useState([]);
    const [collaboratorOptions, setCollaboratorOptions] = useState([]);
    const [showCollaboratorDropdown, setShowCollaboratorDropdown] = useState(false);
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [notes, setNotes] = useState('');
    const [alertConfig, setAlertConfig] = useState({ show: false, type: 'success', title: 'Notification', message: '', onConfirm: () => { } });
    const userId = localStorage.getItem('userId') || '34594';
    const TOKEN = "-2295521862261168";

    const whereInputRef = useRef(null);
    const googleAutocompleteRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Entity Selection State
    const [entityType, setEntityType] = useState(task?.entityType || 'lead');
    const [entityName, setEntityName] = useState(task?.entityName || '');
    const [entityId, setEntityId] = useState(propLeadId || task?.EntityId || task?.leadId || task?.enquiryId || task?.id || "");
    const [relatedSuggestions, setRelatedSuggestions] = useState([]);
    const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);
    const [showAssociatedDropdown, setShowAssociatedDropdown] = useState(false);
    const [associatedSearch, setAssociatedSearch] = useState('');

    const [leadContactDetails, setLeadContactDetails] = useState({ emails: [], mobileNumbers: [] });
    const [selectedContact, setSelectedContact] = useState(''); // Stores selected email or mobile number

    const activeLeadId = entityId;
    
    // Sync state with props
    useEffect(() => {
        if (task?.entityName) setEntityName(task.entityName);
        if (task?.entityType) setEntityType(task.entityType);
        const newId = propLeadId || task?.entityId || task?.EntityId || task?.leadId || task?.enquiryId || task?.id;
        if (newId) setEntityId(newId);
    }, [task?.entityName, task?.entityType, task?.entityId, task?.EntityId, task?.leadId, task?.enquiryId, task?.id, propLeadId]);

    // API Bases
    const API_BASE = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const API_BASE1 = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const NEW_BASE_URL_SERVICE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE3 = (process.env.REACT_APP_ASSETS_BASE_URL || '').replace(/\/$/, '');
    const RELATED_TO = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.TASKS.RELATED_TO_SUGGETION}`;
    const LeadDetails = `${API_BASE}${API_ENDPOINTS.COMMON.GET_LEAD_BY_ID}`;
    const SALES_ACTIVITY_TYPE = `${API_BASE}${API_ENDPOINTS.TASKS.GET_SALES_ACTIVITY_TYPE}`;
    const Owner = `${API_BASE}${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
    const ADD_Physical_Appointment = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.ADD_PHYSICAL_APPOINTMENT}`;
    const Physical_Appointment_Update = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_APPOINTMENT_UPDATE}`;
    const save_deal = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.COMMON.SAVE_DEAL}`;
    const GetWebFormListByUserId = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.COMMON.GET_WEBFORM_LIST_BYUSERID}`;
     const GET_PHYSICAL_APPOINTMENT_TYPE = `${API_BASE}${API_ENDPOINTS.COMMON.GET_TASK_PHYSICAL_APPOINTMENT_TYPE}`;

    useEffect(() => {
        if (task?.actionType === 'appointment') {
            const scriptId = 'google-maps-script';
            const isGoogleMapsReady = () => window.google && window.google.maps && window.google.maps.places;

            if (isGoogleMapsReady()) {
                setScriptsLoaded(true);
            } else if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAO-dLMQIXYLvJeig0dbjeEqytvPg6EMd8&libraries=places&loading=async&callback=initGoogleMapsCallback`;
                script.async = true;
                script.defer = true;
                window.initGoogleMapsCallback = () => {
                    if (isMounted.current) {
                        setScriptsLoaded(true);
                    }
                };
                script.onerror = () => console.error("Google Maps script failed to load");
                document.head.appendChild(script);
            } else {
                const checkInterval = setInterval(() => {
                    if (isGoogleMapsReady() && isMounted.current) {
                        setScriptsLoaded(true);
                        clearInterval(checkInterval);
                    }
                }, 100);
                setTimeout(() => clearInterval(checkInterval), 10000);
            }
        }
        return () => {
            // Use no-op instead of delete to prevent "Script error" if it loads late
            window.initGoogleMapsCallback = () => { };
        };
    }, [task?.actionType]);

    useEffect(() => {
        if (scriptsLoaded && task?.actionType === 'appointment' && whereInputRef.current) {
            if (!window.google || !window.google.maps || !window.google.maps.places) return;

            googleAutocompleteRef.current = new window.google.maps.places.Autocomplete(whereInputRef.current, {
                types: ['geocode', 'establishment'],
            });

            googleAutocompleteRef.current.addListener('place_changed', () => {
                const place = googleAutocompleteRef.current.getPlace();
                if (place && isMounted.current) {
                    const address = place.formatted_address || place.name || '';
                    setAppointmentData(prev => ({
                        ...prev,
                        Where: address,
                        Latitude: place.geometry?.location?.lat() || '',
                        Longitude: place.geometry?.location?.lng() || ''
                    }));
                }
            });
            return () => {
                if (window.google && window.google.maps && window.google.maps.event && googleAutocompleteRef.current) {
                    window.google.maps.event.clearInstanceListeners(googleAutocompleteRef.current);
                }
            };
        }
    }, [scriptsLoaded, task?.actionType]);

    const fetchLeadContactDetails = async (id) => {
        if (!id) return;
        try {
            const response = await fetch(LeadDetails, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ LeadId: id })
            });
            const result = await response.json();
            if (result.d && isMounted.current) {
                const data = typeof result.d === 'string' ? JSON.parse(result.d) : result.d;
                const lead = Array.isArray(data) ? data[0] : (data.Details || data);
                const emails = lead.EmailId ? [lead.EmailId] : [];
                const mobileNumbers = lead.Mobile ? [lead.Mobile] : [];

                setLeadContactDetails({
                    emails: emails,
                    mobileNumbers: mobileNumbers
                });

                if (emails.length > 0) setSelectedContact(emails[0]);
                else if (mobileNumbers.length > 0) setSelectedContact(mobileNumbers[0]);
            }
        } catch (error) { console.error("Error fetching lead details:", error); }
    };

    const fetchRelatedSuggestions = async (text, type) => {
        if (!text || text.length < 1) {
            setRelatedSuggestions([]);
            setShowRelatedDropdown(false);
            return;
        }
        try {
            const response = await fetch(RELATED_TO, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: {
                        UserId: parseInt(userId),
                        FilterText: text,
                        TaskType: type.toLowerCase()
                    }
                })
            });
            const result = await response.json();
            const rawData = result.Details || result.d || result.Data || result.data || result;
            if (rawData && isMounted.current) {
                const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                setRelatedSuggestions(Array.isArray(parsed) ? parsed : []);
                setShowRelatedDropdown(true);
            }
        } catch (error) { console.error("Error fetching suggestions:", error); }
    };

    // 1. Followup State & Logic
    const [followupData, setFollowupData] = useState({
        Status: '1290',
        AssignedTo: userId,
        DateTime: new Date().toISOString().slice(0, 16),
        Remarks: '',
        Products: '',
        IsReAssign: false
    });
    const [followupStatusOptions, setFollowupStatusOptions] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [productSuggestions, setProductSuggestions] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    const renderAvatar = (name, url = null, size = 'sm', borderRadius = '50%') => {
        if (url && !url.includes('img_avatar1.png')) {
            return <img src={url} alt="" style={{ width: size === 'lg' ? '60px' : '40px', height: size === 'lg' ? '60px' : '40px', borderRadius: borderRadius, objectFit: 'cover' }} />;
        }
        const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : '??';
        const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a69a', '#4caf50', '#ff9800', '#ff5722'];
        const colorIdx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % colors.length;
        const bgColor = colors[colorIdx];

        return (
            <div className={`avatar-initials-circle ${size}`} style={{
                backgroundColor: bgColor,
                position: 'relative',
                width: size === 'lg' ? '60px' : '40px',
                height: size === 'lg' ? '60px' : '40px',
                borderRadius: borderRadius,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: size === 'lg' ? '24px' : '14px'
            }}>
                {initials}
            </div>
        );
    };

    const fetchFollowupStatuses = async () => {
        try {
            const url = `${API_BASE}${API_ENDPOINTS.APPOINTMENTS.FOLLOWUPSTATUS_LIST}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({ strParentID: userId })
            });
            const result = await response.json();
            if (result.d && isMounted.current) {
                const parsed = typeof result.d === 'string' ? JSON.parse(result.d) : result.d;
                setFollowupStatusOptions(Array.isArray(parsed) ? parsed : []);
            }
        } catch (error) { console.error("Error fetching followup statuses:", error); }
    };

    const fetchProductSuggestions = async (text) => {
        if (!text || text.length < 1) {
            setProductSuggestions([]);
            setShowProductDropdown(false);
            return;
        }
        try {
            const url = `${API_BASE1}/${API_ENDPOINTS.APPOINTMENTS.PRODUCT_SUGGESTION_FOR_FOLLOWUP}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: { UserId: parseInt(userId), Term: text }
                })
            });
            const result = await response.json();
            if (result.d && isMounted.current) {
                const parsed = typeof result.d === 'string' ? JSON.parse(result.d) : result.d;
                setProductSuggestions(Array.isArray(parsed) ? parsed : []);
                setShowProductDropdown(true);
            }
        } catch (error) { console.error("Error fetching product suggestions:", error); }
    };

    const handleSaveFollowup = async () => {
        if (!followupData.Status) { alert("Please select a status."); return; }
        setIsSubmitting(true);
        try {
            const [date, time] = followupData.DateTime.split('T');
            const newDate = new Date(`${date}T${time || "00:00"}`);
            const formattedNextStatusDate = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')} ${String(newDate.getHours()).padStart(2, '0')}:${String(newDate.getMinutes()).padStart(2, '0')}:${String(newDate.getSeconds()).padStart(2, '0')}`;

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    LeadId: parseInt(activeLeadId),
                    AssignedTo: parseInt(followupData.AssignedTo || userId),
                    ParentID: parseInt(userId),
                    FupValue: "",
                    FollowUpStatus: parseInt(followupData.Status),
                    NextStatusDate: formattedNextStatusDate,
                    Remarks: followupData.Remarks,
                    Products: followupData.Products,
                    AmountPaid: 0.0,
                    CreatedBy: parseInt(userId),
                    IsReAssign: !!followupData.IsReAssign,
                    NotifyBySMS: false,
                    NotifyByEmail: false,
                    SmsScheduelDateTime: "",
                    EmailScheduelDateTime: ""
                }
            };
            const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
            const url = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.ADD_LEAD_FOLLOWUP}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                const resData = await response.json();
                console.log("Save Followup Response:", resData);
                if (resData && (resData.Status === 1 || resData.Status === 200 || (resData.Message && resData.Message.toLowerCase().includes("success")))) {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: 'Followup saved successfully!',
                        onConfirm: () => onBack()
                    });
                } else {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: resData?.Message || "Failed to save followup.",
                        onConfirm: () => {}
                    });
                }
            } else {
                setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Server Error',
                    message: "Server error. Please try again.",
                    onConfirm: () => {}
                });
            }
        } catch (error) {
            console.error("Error saving followup:", error);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 2. SMS State & Logic
    const [sendSmsData, setSendSmsData] = useState({
        ChooseNumbers: [],
        Account: '',
        SenderId: '',
        TemplateId: '',
        Message: '',
        IsUnicode: false,
        Message: '',
        IsUnicode: false,
        SmsType: 'Compose',
        SelectedNumber: ''
    });
    const [smsAccounts, setSmsAccounts] = useState([]);
    const [smsSenderIds, setSmsSenderIds] = useState([]);
    const [smsTemplates, setSmsTemplates] = useState([]);

    const fetchSmsData = async () => {
        try {
            // SMS Accounts
            const accRes = await fetch(`${API_BASE1}${API_ENDPOINTS.COMMON.GET_MAIL_SMS_DLT_ACCOUNT}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentId: userId, IsSms: true } })
            });
            const accData = await accRes.json();
            if (isMounted.current) {
                setSmsAccounts(accData.Details || []);
            }

            // Sender IDs
            const senderRes = await fetch(`${API_BASE1}/${API_ENDPOINTS.APPOINTMENTS.GET_SENDER_IDFOR_SMS}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(userId) } })
            });
            const senderData = await senderRes.json();
            if (isMounted.current) {
                setSmsSenderIds(senderData.Details || []);
            }

            // Templates
            const tempRes = await fetch(`${API_BASE}/${API_ENDPOINTS.COMMON.NEW_GET_TEMPLATE_FOR_MAIL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentID: parseInt(userId), isSMS: 1, isMail: 0 } })
            });
            const tempData = await tempRes.json();
            if (isMounted.current) {
                setSmsTemplates(tempData.Details || []);
            }
        } catch (e) { console.error(e); }
    };

    const handleSendSms = async () => {
        if (!sendSmsData.Message) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please enter a message.", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    ParentId: userId,
                    MobileNos: [sendSmsData.SelectedNumber || task?.mobile || ""],
                    Message: sendSmsData.Message,
                    AccountId: sendSmsData.Account,
                    SenderId: sendSmsData.SenderId,
                    TemplateId: sendSmsData.TemplateId,
                    IsUnicode: sendSmsData.IsUnicode ? 1 : 0,
                    LeadId: activeLeadId
                }
            };
            const res = await fetch(`${API_BASE}/${API_ENDPOINTS.COMMON.NEW_SEND_SMS}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const data = await res.json();
                const result = data.d ? (typeof data.d === 'string' ? JSON.parse(data.d) : data.d) : data;

                if (result && (result.Status === 1 || result.Status === 200 || (result.Message && result.Message.toLowerCase().includes("success")))) {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: "SMS sent successfully!",
                        onConfirm: () => onBack()
                    });
                } else {
                    if (isMounted.current) setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: result?.Message || "Failed to send SMS.",
                        onConfirm: () => {}
                    });
                }
            } else {
                if (isMounted.current) setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Server Error',
                    message: "Server error. Please try again.",
                    onConfirm: () => {}
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 3. Mail State & Logic
    const [sendMailData, setSendMailData] = useState({
        Account: '',
        From: '',
        ReplyTo: '',
        To: '',
        Cc: '',
        Subject: '',
        TemplateId: '',
        Body: '',
        SelectedEmail: ''
    });
    const [mailAccounts, setMailAccounts] = useState([]);
    const [fromMailOptions, setFromMailOptions] = useState([]);
    const [mailTemplates, setMailTemplates] = useState([]);

    const fetchMailData = async () => {
        try {
            // Mail Accounts
            const accRes = await fetch(`${API_BASE1}${API_ENDPOINTS.COMMON.GET_MAIL_SMS_DLT_ACCOUNT}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentId: parseInt(userId), isSMS: false } })
            });
            const accData = await accRes.json();
            if (isMounted.current) {
                setMailAccounts(accData.Details || []);
            }

            // Templates
            const tempRes = await fetch(`${NEW_BASE_URL_SERVICE}/${API_ENDPOINTS.COMMON.NEW_GET_TEMPLATE_FOR_MAIL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentId: parseInt(userId), isSMS: 0, isMail: 1 } })
            });
            const tempData = await tempRes.json();
            if (isMounted.current) {
                setMailTemplates(tempData.Details || []);
                if (task?.email) setSendMailData(prev => ({ ...prev, To: task.email }));
            }
        } catch (e) { console.error(e); }
    };

    const fetchFromMailIds = async () => {
        try {
            const res = await fetch(`${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.COMMON.GET_FROM_MAIL_ID}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Status: "", Message: "", Token: "-2295521862261168", Details: { UserId: parseInt(userId) } })
            });
            const data = await res.json();
            if (isMounted.current) {
                setFromMailOptions(data.Details || []);
            }
        } catch (e) { console.error(e); }
    };

    const handleSendMail = async () => {
        if (!sendMailData.Subject) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please enter a subject.", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    AccountId: parseInt(sendMailData.Account || userId),
                    UserId: parseInt(userId),
                    LeadId: activeLeadId.toString(),
                    TemplateId: parseInt(sendMailData.TemplateId || 0),
                    Subject: sendMailData.Subject,
                    Messages: sendMailData.Body,
                    SenderName: "CRM",
                    ReplyTo: sendMailData.ReplyTo || "",
                    FromMailId: sendMailData.From || "",
                    EmailIds: [sendMailData.SelectedEmail || sendMailData.To || ""]
                }
            };
            const res = await fetch(`${API_BASE}/${API_ENDPOINTS.COMMON.NEW_SEND_MAIL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: "Email sent successfully!",
                    onConfirm: () => onBack()
                });
            } else {
                setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: "Failed to send email.",
                    onConfirm: () => {}
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 4. Voice State & Logic
    const [sendVoiceData, setSendVoiceData] = useState({
        ChooseNumbers: [],
        DnidNo: '',
        AppFlow: '',
        Route: 'trans',
        SelectedNumber: ''
    });
    const [dniNumbers, setDniNumbers] = useState([]);
    const [appFlows, setAppFlows] = useState([]);

    const fetchVoiceData = async () => {
        try {
            // DNI Numbers
            const dniRes = await fetch(`${NEW_BASE_URL_SERVICE}/${API_ENDPOINTS.COMMON.NEW_SEND_VOICE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentId: parseInt(userId), AppId: 2, Opt: 0 } })
            });
            const dniData = await dniRes.json();
            if (isMounted.current) {
                setDniNumbers(dniData.Details || []);
            }

            // App Flows
            const appRes = await fetch(`${NEW_BASE_URL_SERVICE}/${API_ENDPOINTS.COMMON.NEW_SEND_VOICE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { ParentId: parseInt(userId), AppId: 0, Opt: 2 } })
            });
            const appData = await appRes.json();
            if (isMounted.current) {
                setAppFlows(appData.Details || []);
                if (task?.mobile) setSendVoiceData(prev => ({ ...prev, ChooseNumbers: [task.mobile] }));
            }
        } catch (e) { console.error(e); }
    };

    const handleSendVoice = async () => {
        if (sendVoiceData.AppFlow === "" || sendVoiceData.AppFlow === undefined || sendVoiceData.AppFlow === null) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please select an app flow.", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: JSON.stringify({
                    Route: sendVoiceData.Route,
                    AppId: parseInt(sendVoiceData.AppFlow),
                    CallerId: sendVoiceData.DnidNo,
                    MobileNos: [sendVoiceData.SelectedNumber || (sendVoiceData.ChooseNumbers[0] || task?.mobile || "")].map(n => n.replace(/\D/g, '').slice(-10))
                })
            };
            const res = await fetch(`${NEW_BASE_URL_SERVICE}/${API_ENDPOINTS.COMMON.ADD_VOICE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const data = await res.json();
                const result = data.d ? (typeof data.d === 'string' ? JSON.parse(data.d) : data.d) : data;

                if (result && (result.Status === 1 || result.Status === 200 || (result.Message && result.Message.toLowerCase().includes("success")))) {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: "Voice call initiated!",
                        onConfirm: () => onBack()
                    });
                } else {
                    if (isMounted.current) setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: result?.Message || "Failed to initiate voice call.",
                        onConfirm: () => {}
                    });
                }
            } else {
                if (isMounted.current) setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Server Error',
                    message: "Server error. Please try again.",
                    onConfirm: () => {}
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 5. Add Task/Appointment State & Logic
    const [addTaskData, setAddTaskData] = useState({
        Title: `NewTask_${new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)}`,
        Description: '',
        Remarks: '',
        SalesActivityType: '7',
        DueDate: new Date().toISOString().split('T')[0],
        DueTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        Owner: userId,
        Collaborators: ''
    });

    const [appointmentData, setAppointmentData] = useState({
        Title: `NewAppointment_${new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)}`,
        ActivityType: '505',
        Description: '',
        Remarks: '',
        StartDate: new Date().toISOString().split('T')[0],
        StartTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        EndDate: new Date().toISOString().split('T')[0],
        EndTime: new Date(new Date().getTime() + 60 * 60 * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        Where: '',
        CompleteAddress: '',
        FieldExecutive: userId,
        Owner: userId,
        Collaborators: [],
        Latitude: '20.593684',
        Longitude: '78.96288'
    });

    const handleAppointmentChange = (e) => {
        const { name, value } = e.target;
        if (name === 'Remarks') {
            setNotes(value);
            setFollowupData(prev => ({ ...prev, Remarks: value }));
            setAddTaskData(prev => ({ ...prev, Remarks: value }));
        }
        setAppointmentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const fetchSalesActivityTypes = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const res = await fetch(`${API_BASE}${API_ENDPOINTS.TASKS.GET_SALES_ACTIVITY_TYPE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ Token: TOKEN || "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            const data = await res.json();
            const result = data.d || data.Details || [];
            if (isMounted.current) {
                setSalesActivityOptions(Array.isArray(result) ? result : []);
            }
        } catch (e) { console.error(e); }
    };

    const [physicalAppointmentTypes, setPhysicalAppointmentTypes] = useState([]);

    const fetchPhysicalAppointmentTypes = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const res = await fetch(GET_PHYSICAL_APPOINTMENT_TYPE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ Token: TOKEN || "-2295521862261168", Details: { UserId: parseInt(uid, 10), Mode: "Task,VirtualApt,PhysicalMeeting" } })
            });
            const data = await res.json();
            const result = data.d || data.Details || [];
            if (isMounted.current) {
                const filtered = (Array.isArray(result) ? result : []).filter(item => item.Code === 'PhysicalMeeting');
                setPhysicalAppointmentTypes(filtered);
                if ((!task || task.isNew) && filtered.length > 0) {
                    setAppointmentData(prev => ({ ...prev, ActivityType: String(filtered[0].Id || filtered[0].Code) }));
                }
            }
        } catch (e) { console.error(e); }
    };

    const fetchCollaborators = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const response = await fetch(`${NEW_BASE_URL_SERVICE}/Common/GetUserListByUserId`, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify({ Token: TOKEN || "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            if (response.ok) {
                const result = await response.json();
                const rawData = result.Details?.data || result.Details || result.d || [];
                const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                const data = Array.isArray(parsed) ? parsed.map(u => ({ Code: u.User_ID || u.Code || u.Id, Name: u.Name || u.Text })) : [];
                if (isMounted.current) {
                    setCollaboratorOptions(data);
                }
            }
        } catch (error) {
            console.error("Error fetching collaborators:", error);
        }
    };

    const handleSaveTask = async (isAppointment = false) => {
        const data = isAppointment ? appointmentData : addTaskData;
        if (!data.Title.trim()) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please enter a title.", onConfirm: () => { } });
            return;
        }

        if (isAppointment) {
            const start = new Date(`${data.StartDate}T${data.StartTime}`);
            const end = new Date(`${data.EndDate}T${data.EndTime}`);
            if (start >= end) {
                setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Start date must be smaller than end date", onConfirm: () => { } });
                return;
            }
        }
        setIsSubmitting(true);
        try {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const formatForAPI = (dateStr, timeStr) => {
                if (!dateStr || !timeStr) return "";
                const d = new Date(`${dateStr}T${timeStr}`);
                return `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:00`;
            };

            const formatForSave = (dateStr, timeStr) => {
                if (!dateStr || !timeStr) return "";
                const d = new Date(`${dateStr}T${timeStr}`);
                return `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
            };

            let payload;
            if (isAppointment) {
                payload = {
                    Token: TOKEN,
                    LoggedUserId: userId,
                    Details: {
                        Mode: task?.isNew ? "PI" : "PU",
                        PhysicalAppointmentId: task?.isNew ? 0 : (task?.PhysicalAppointmentId || task?.id || 0).toString().split('_')[0],
                        Title: data.Title,
                        Description: data.Description,
                        Address: data.Where || "",
                        AssignedTo: parseInt(data.Owner),
                        CollaboratorList: Array.isArray(data.Collaborators) ? data.Collaborators.join(',') : (typeof data.Collaborators === 'string' ? data.Collaborators.split(',').join(',') : (data.Collaborators || "")),
                        CompleteAddress: data.CompleteAddress || "",
                        CreatedBy: parseInt(userId),
                        EmailId: data.EmailId || "",
                        EndDate: formatForSave(data.EndDate, data.EndTime),
                        EntityId: (entityId || task?.EntityId || task?.leadId || task?.enquiryId || "").toString(),
                        EntityName: entityName || task?.EntityName || task?.name || "",
                        EntityType: entityType || task?.entityType || "lead",
                        FieldExecutiveList: (data.FieldExecutive || userId).toString(),
                        GeoEntitySiteId: 0,
                        Latitude: parseFloat(data.Latitude || 20.593684),
                        Longitude: parseFloat(data.Longitude || 78.96288),
                        Mobile: data.Mobile || "",
                        SenderName: "",
                        StartDate: formatForSave(data.StartDate, data.StartTime),
                        TaskTypeId: parseInt(data.ActivityType) || 505,
                        gCalendarId: ""
                    }
                };
            } else {
                payload = {
                    Token: TOKEN,
                    Details: {
                        insertUpdateTask: {
                            Id: task?.isNew ? 0 : (task?.id || 0),
                            Title: data.Title,
                            Description: data.Description,
                            Remarks: data.Remarks,
                            Address: '',
                            StartDate: "",
                            AssignedTo: parseInt(data.Owner),
                            BySMS: false,
                            CollabratoerUserIds: Array.isArray(data.Collaborators) ? data.Collaborators.join(',') : (typeof data.Collaborators === 'string' ? data.Collaborators.split(',').join(',') : (data.Collaborators || "")),
                            CompleteAddress: "",
                            CreatedBy: 0,
                            DLTTemplateIDForNotify: "",
                            DLTTemplateIDForSMSReminder: "",
                            EndDate: formatForAPI(data.DueDate, data.DueTime),
                            IsActive: true,
                            IsAppointment: false,
                            IsEmail: false,
                            IsSMS: false,
                            IsTask: true,
                            LeadId: parseInt(task?.EntityId || task?.enquiryId || task?.leadId || task?.id || activeLeadId || "2508095"),
                            Longitude: "",
                            Latitude: "",
                            NotifyMe: false,
                            OutcomesId: 0,
                            Status: "open",
                            TaskId: parseInt(data.SalesActivityType) || 0,
                            TaskType: "",
                            Token: TOKEN
                        },
                        TimeZone: "India Standard Time"
                    }
                };
            }

            console.log("Saving Task/Appointment Payload:", payload);

            const targetUrl = (isAppointment && !task?.isNew) ? Physical_Appointment_Update : ADD_Physical_Appointment;

            const res = await fetch(targetUrl, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json, text/javascript, */*; q=0.01"
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const text = await res.text();
                console.log("Save Response Status:", res.status, "Body Preview:", text.substring(0, 100));

                let isSuccess = false;
                let responseData;
                try {
                    const json = JSON.parse(text);
                    responseData = json.d ? (typeof json.d === 'string' ? JSON.parse(json.d) : json.d) : json;

                    if (responseData && (
                        responseData.IsInserted ||
                        responseData.IsUpdated ||
                        responseData.Status === 1 ||
                        responseData.Status === 200 ||
                        (responseData.Message && responseData.Message.toLowerCase().includes("success")) ||
                        text.includes('"IsInserted":true') ||
                        text.includes('"IsUpdated":true') ||
                        text.toLowerCase().includes("success")
                    )) {
                        isSuccess = true;
                    }
                } catch (e) {
                    if (text.toLowerCase().includes("success") || text.includes('"IsInserted":true') || text.includes('"IsUpdated":true')) {
                        isSuccess = true;
                    }
                }

                if (isSuccess) {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: isAppointment ? 'Physical Appointment Added successfully!' : 'Task created successfully!',
                        onConfirm: () => onBack()
                    });
                } else {
                    setIsSubmitting(false);
                    console.warn("Save response failure:", text);
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to save.',
                        onConfirm: () => {}
                    });
                }
            } else {
                setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Server Error',
                    message: 'Server error. Please try again.',
                    onConfirm: () => {}
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 6. Deal State & Logic
    const [dealData, setDealData] = useState({
        Amount: '',
        PipelineId: '',
        PipelineStage: '',
        Probability: '',
        DealOwner: userId,
        ClosureDate: new Date().toISOString().split('T')[0]
    });
    const [dealPipelines, setDealPipelines] = useState([]);
    const [dealStages, setDealStages] = useState([]);

    const fetchDealData = async () => {
        try {
            const res = await fetch(`${API_BASE}/UserCRMCampaign/Service/IMSService.asmx/getDealPipeline`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pUserId: parseInt(userId) })
            });
            const data = await res.json();
            if (isMounted.current) {
                setDealPipelines(data.d || []);
            }
        } catch (e) { console.error(e); }
    };

    const fetchDealStages = async (pipeId) => {
        try {
            const res = await fetch(`${API_BASE}/UserCRMCampaign/Service/IMSService.asmx/getDealPipelineStage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pipelineId: pipeId.toString() })
            });
            const data = await res.json();
            if (isMounted.current) {
                setDealStages(data.d || []);
            }
        } catch (e) { console.error(e); }
    };

    const handleSaveDeal = async () => {
        if (!dealData.Amount || !dealData.PipelineId) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please fill required fields.", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const d = new Date(dealData.ClosureDate);
            const formattedDate = `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;

            const payload = {
                Status: "",
                Message: "",
                Token: "-2295521862261168",
                Details: {
                    LeadId: activeLeadId.toString(),
                    PipelineId: parseInt(dealData.PipelineId),
                    PipelineStage: parseInt(dealData.PipelineStage),
                    Amount: parseFloat(dealData.Amount),
                    ClosureDate: formattedDate,
                    DealOwner: parseInt(dealData.DealOwner),
                    Probability: dealData.Probability.toString(),
                    UserId: parseInt(userId)
                }
            };
            const res = await fetch(save_deal, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const text = await res.text();
                let isSuccess = false;
                try {
                    const json = JSON.parse(text);
                    const data = json.d ? (typeof json.d === 'string' ? JSON.parse(json.d) : json.d) : json;
                    if (json.Status === 1 || json.Status === "1" || data.Status === 1 || data.Status === "1" || data.IsInserted === true) {
                        isSuccess = true;
                    }
                } catch (e) {
                    if (text.includes('"Status":1') || text.includes('Status": 1')) isSuccess = true;
                }

                if (isSuccess) {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: "Deal saved successfully!",
                        onConfirm: () => onBack()
                    });
                } else {
                    setIsSubmitting(false);
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: "Failed to save deal.",
                        onConfirm: () => {}
                    });
                }
            } else {
                setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Server Error',
                    message: "Server error. Please try again.",
                    onConfirm: () => {}
                });
            }
        } catch (error) {
            console.error(error);
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: "An error occurred while saving.",
                onConfirm: () => {}
            });
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    // 7. Webform State & Logic
    const [webforms, setWebforms] = useState([]);
    const [selectedWebform, setSelectedWebform] = useState('');

    const fetchWebforms = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Status: "",
                Message: "",
                Token: TOKEN,
                Details: {
                    UserId: Number(uid),
                    FilterText: "",
                    OffsetValue: 0,
                    PagingSize: 100
                }
            };
            const response = await fetch(GetWebFormListByUserId, {
                method: 'POST',
                headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json();
            const data = result.Details || result.d || result.data || result;

            if (isMounted.current) {
                if (Array.isArray(data)) {
                    setWebforms(data);
                } else if (data && typeof data === 'string') {
                    try {
                        const parsed = JSON.parse(data);
                        if (Array.isArray(parsed)) setWebforms(parsed);
                    } catch (e) {
                        console.error("Failed to parse stringified webform data", e);
                    }
                }
            }
        } catch (error) { console.error("Error fetching webforms:", error); }
    };

    const handleFillWebform = () => {
        if (!selectedWebform) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please select a webform", onConfirm: () => { } });
            return;
        }
        const form = webforms.find(f => (f.Id || f.Code || f.WebFormId) == selectedWebform);
        if (form) {
            const formID = form.Id || form.Code || form.WebFormId;
            const formName = form.Text || form.Name || form.WebFormName;
            const url = `https://kit19.com/UserCRMCampaign/webFormDetails.aspx?ParentID=${userId}&cName=${encodeURIComponent(formName)}&EntityName=Lead&AgentId=${userId}&EntityId=${activeLeadId}`;
            window.open(url, '_blank');
            onBack();
        }
    };

    // 8. Notes & Upload State & Logic
    const [uploadData, setUploadData] = useState({ Title: '', File: null, FileName: '' });

    const handleAddNotes = async () => {
        if (!notes.trim()) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please enter some notes", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = { LeadID: activeLeadId.toString(), Notes: notes };
            const response = await fetch('https://kit19.com/UserCRMCampaign/Service/CRMService.asmx/AddNotes', {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                if (isMounted.current) {
                    setIsSubmitting(false);
                    setNotes('');
                }
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: "Notes added successfully!",
                    onConfirm: () => onBack()
                });
            } else {
                if (isMounted.current) setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: "Failed to add notes",
                    onConfirm: () => {}
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (isMounted.current) {
                setIsSubmitting(false);
                setNotes('');
            }
        }
    };

    const handleUpload = async () => {
        if (!uploadData.File) {
            setAlertConfig({ show: true, type: 'warning', title: 'Validation', message: "Please select a file", onConfirm: () => { } });
            return;
        }
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('File', uploadData.File);
            formData.append('Title', uploadData.Title);
            formData.append('LeadId', activeLeadId);
            formData.append('UserId', userId);
            const response = await fetch(`${API_BASE}/UserCRMCampaign/Service/CRMService.asmx/UploadLeadDocument`, {
                method: 'POST',
                body: formData
            });
            if (response.ok) {
                if (isMounted.current) setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: "Document uploaded successfully!",
                    onConfirm: () => onBack()
                });
            } else {
                if (isMounted.current) setIsSubmitting(false);
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: "Failed to upload document",
                    onConfirm: () => {}
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const url = `${NEW_BASE_URL_SERVICE}/TicketSupport/BindUsersForLeadPupup`;
            const response = await fetch(url, { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify({ Token: TOKEN || "-2295521862261168", Details: { UserId: parseInt(userId, 10) } }) 
            });
            const result = await response.json();
            const rawData = result.Details?.data || result.Details || result.d || [];
            const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
            
            if (isMounted.current) {
                setUserList(Array.isArray(parsed) ? parsed.map(u => ({ 
                    UserId: u.User_ID || u.Code || u.UserId || u.Id, 
                    UserName: u.Name || u.Text || u.UserName 
                })) : []);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        fetchUsers();
        const type = task?.actionType || 'followup';
        if (type === 'followup') fetchFollowupStatuses();
        if (type === 'sms') fetchSmsData();
        if (type === 'mail') {
            fetchMailData();
            fetchFromMailIds();
        }
        if (type === 'voice') fetchVoiceData();
        if (type === 'task' || type === 'appointment') {
            fetchSalesActivityTypes();
            fetchCollaborators();
            if (type === 'appointment') {
                fetchPhysicalAppointmentTypes();
            }
        }
        if (type === 'deal') fetchDealData();
        if (type === 'webform') fetchWebforms();

        // Populate for EDIT mode
        if (task && !task.isNew) {
            if (type === 'appointment') {
                const parseDate = (str) => {
                    if (!str) return { date: '', time: '' };
                    try {
                        const d = new Date(str);
                        if (isNaN(d.getTime())) return { date: '', time: '' };
                        return {
                            date: d.toISOString().split('T')[0],
                            time: d.toTimeString().split(' ')[0].substring(0, 5)
                        };
                    } catch (e) { return { date: '', time: '' }; }
                };
                const start = parseDate(task.startDate || task.StartDate);
                const end = parseDate(task.endDate || task.EndDate);

                setAppointmentData({
                    Title: task.taskTitle || task.Title || '',
                    ActivityType: String(task.TaskId || task.taskId || task.TaskTypeId || '505'),
                    Description: task.description || task.Description || '',
                    Remarks: task.remarks || task.Remarks || '',
                    StartDate: start.date,
                    StartTime: start.time,
                    EndDate: end.date,
                    EndTime: end.time,
                    Where: task.address || task.Address || '',
                    Owner: String(task.assignedTo || task.AssignedTo || userId),
                    Collaborators: task.collaboratorList ? task.collaboratorList.split(/[#,]/).filter(Boolean) : (task.CollaboratorUserIds ? task.CollaboratorUserIds.split(/[#,]/).filter(Boolean) : []),
                    Latitude: task.Latitude || '',
                    Longitude: task.Longitude || ''
                });
            } else if (type === 'task') {
                const due = (str) => {
                    if (!str) return { date: '', time: '' };
                    const d = new Date(str);
                    return { date: d.toISOString().split('T')[0], time: d.toTimeString().split(' ')[0].substring(0, 5) };
                };
                const dParsed = due(task.endDate || task.EndDate);
                setAddTaskData({
                    Title: task.taskTitle || task.Title || '',
                    Description: task.description || task.Description || '',
                    Remarks: task.remarks || task.Remarks || '',
                    SalesActivityType: String(task.TaskId || task.taskId || ''),
                    DueDate: dParsed.date,
                    DueTime: dParsed.time,
                    Owner: String(task.assignedTo || task.AssignedTo || userId),
                    Collaborators: task.collaboratorList || task.CollaboratorUserIds || ''
                });
            }
        }
    }, [task]);


    // Render Helpers
    const getHeaderTitle = () => {
        switch (task?.actionType) {
            case 'followup': return 'Add FollowUp';
            case 'mail': return 'Send Mail';
            case 'sms': return 'Send SMS';
            case 'voice': return 'Send Voice';
            case 'task': return 'Add Task';
            case 'appointment': return 'Add Appointment';
            case 'deal': return 'Add Deal';
            case 'webform':
            case 'fill-webform': return 'Fill Webform';
            // case 'notes':
            // case 'add-notes': return 'Add Notes';
            case 'upload': return 'Upload Document';
            default: return 'Action';
        }
    };

    const renderEntitySelection = () => (
        <div className="entity-selection-section">
            <div className="form-row">
                <div className="form-group flex-1">
                    <label className="input-label" style={{ color: '#64748b', fontWeight: '500' }}>Associated to</label>
                    <div className="custom-searchable-select">
                        <div className="select-trigger" onClick={() => setShowAssociatedDropdown(!showAssociatedDropdown)}>
                            <span>{entityType.charAt(0).toUpperCase() + entityType.slice(1)}</span>
                            <span>▼</span>
                        </div>
                        {showAssociatedDropdown && (
                            <div className="select-dropdown">
                                <div className="select-search-box">
                                    <input
                                        type="text"
                                        className="select-search-input"
                                        placeholder="Search..."
                                        value={associatedSearch}
                                        onChange={(e) => setAssociatedSearch(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className="select-options-list">
                                    {['Lead', 'Enquiry', 'Site'].filter(opt => opt.toLowerCase().includes(associatedSearch.toLowerCase())).map(opt => (
                                        <div
                                            key={opt}
                                            className={`select-option ${entityType === opt.toLowerCase() ? 'active' : ''}`}
                                            onClick={() => {
                                                setEntityType(opt.toLowerCase());
                                                setEntityId('');
                                                setEntityName('');
                                                setShowAssociatedDropdown(false);
                                                setAssociatedSearch('');
                                            }}
                                        >
                                            {opt}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="form-row">
                <div className="form-group flex-1">
                    <label className="input-label" style={{ color: '#64748b', fontWeight: '500' }}>Entity Related</label>
                    <div className="search-input-wrapper">
                        <input
                            type="text"
                            className="action-input"
                            value={entityName}
                            onChange={(e) => {
                                setEntityName(e.target.value);
                                setEntityId('');
                                fetchRelatedSuggestions(e.target.value, entityType);
                            }}
                            onFocus={() => setShowRelatedDropdown(true)}
                            placeholder="Search related..."
                        />
                        <Search size={18} className="search-input-icon" />
                    </div>
                    {showRelatedDropdown && relatedSuggestions.length > 0 && (
                        <div className="enhanced-suggestions-dropdown">
                            {relatedSuggestions.map((s, idx) => (
                                <div key={idx} className="enhanced-suggestion-item" onClick={() => {
                                    setEntityName(s.Name || s.Text || '');
                                    setEntityId(s.Id || s.Code || '');
                                    setShowRelatedDropdown(false);
                                    fetchLeadContactDetails(s.Id || s.Code);
                                }}>
                                    <div className="suggestion-avatar-wrapper">
                                        <div className="suggestion-avatar">
                                            {renderAvatar(s.Name || s.Text, s.Image, 'sm', '50%')}
                                        </div>
                                    </div>
                                    <div className="suggestion-main-info">
                                        <div className="s-name">{s.Name || s.Text}</div>
                                        <div className="s-meta-row">
                                            {s.Mobile && <span className="s-meta-item"><PhoneCall size={12} /> {s.Mobile}</span>}
                                            {s.City && <span className="s-meta-item"><MapPin size={12} /> {s.City}</span>}
                                            {(!s.Mobile && !s.City) && <span className="s-meta-item">ID: {s.Id || s.Code}</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <hr className="section-divider" />
        </div>
    );

    return (
        <div className="action-operations-container">
            <div className="action-card">
                <div className="action-body">
                    {task?.actionType !== 'appointment' && renderEntitySelection()}
                    {(task?.actionType === 'followup' || !task?.actionType) && (
                        <>
                            <div className="form-row">
                                <label className="input-label">Status</label>
                                <select className="action-select" value={followupData.Status} onChange={(e) => setFollowupData({ ...followupData, Status: e.target.value })}>
                                    <option value="">Select Status</option>
                                    {followupStatusOptions.map(opt => <option key={opt.Code} value={opt.Code}>{opt.Text}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Owner</label>
                                <select className="action-select" value={followupData.AssignedTo} onChange={(e) => setFollowupData({ ...followupData, AssignedTo: e.target.value })}>
                                    {userList.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Date & Time</label>
                                <input type="datetime-local" className="action-input" value={followupData.DateTime} onChange={(e) => setFollowupData({ ...followupData, DateTime: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <label className="input-label">Remarks</label>
                                <textarea className="action-textarea" value={followupData.Remarks} onChange={(e) => setFollowupData({ ...followupData, Remarks: e.target.value })} placeholder="Enter remarks..."></textarea>
                            </div>
                            <div className="form-row">
                                <div className="follow-type-section">
                                    <label className="input-label" style={{ marginBottom: '12px', display: 'block' }}>Follow Type</label>
                                    <label className="checkbox-label">
                                        <input type="checkbox" checked={followupData.IsReAssign} onChange={(e) => setFollowupData({ ...followupData, IsReAssign: e.target.checked })} /> Re Assign
                                    </label>
                                </div>
                            </div>
                            <div className="form-row" style={{ position: 'relative' }}>
                                <label className="input-label">Choose Products *</label>
                                <input type="text" className="action-input" placeholder="Enter product" value={productSearch} onChange={(e) => { setProductSearch(e.target.value); fetchProductSuggestions(e.target.value); }} />
                                {showProductDropdown && productSuggestions.length > 0 && (
                                    <div className="products-dropdown">
                                        {productSuggestions.map((p, idx) => (
                                            <div key={idx} className="suggestion-item" onClick={() => {
                                                setFollowupData({ ...followupData, Products: p.Id || p.Code || p.ProductID || '' });
                                                setProductSearch(p.Name || p.Text || p.ProductName || '');
                                                setShowProductDropdown(false);
                                            }}>
                                                <div className="s-name-bold">{p.Name || p.Text || p.ProductName}</div>
                                                <div className="s-meta-small">ID: {p.Id || p.Code || p.ProductID}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="form-footer">
                                <button className="btn-reset" onClick={() => { setFollowupData({ ...followupData, Status: '1290', Remarks: '', Products: '', IsReAssign: false }); setProductSearch(''); }}>Reset</button>
                                <button className="btn-save" onClick={handleSaveFollowup} disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save'}</button>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'sms' && (
                        <>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>Choose Numbers</label>
                                <div className="contact-selection-list">
                                    {leadContactDetails.mobileNumbers.length > 0 ? (
                                        leadContactDetails.mobileNumbers.map((num, i) => (
                                            <label key={i} className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="selectedNumber"
                                                    checked={sendSmsData.SelectedNumber === num}
                                                    onChange={() => setSendSmsData({ ...sendSmsData, SelectedNumber: num })}
                                                />
                                                {num}
                                            </label>
                                        ))
                                    ) : (
                                        <div className="no-contacts-msg">No numbers found for this lead</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <label className="input-label">SMS Account</label>
                                <select className="action-select" value={sendSmsData.Account} onChange={(e) => setSendSmsData({ ...sendSmsData, Account: e.target.value })}>
                                    <option value="">Select Account</option>
                                    {smsAccounts.map(acc => <option key={acc.AccountId || acc.Code} value={acc.AccountId || acc.Code}>{acc.AccountName || acc.Text}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Sender ID</label>
                                <select className="action-select" value={sendSmsData.SenderId} onChange={(e) => setSendSmsData({ ...sendSmsData, SenderId: e.target.value })}>
                                    <option value="">Select Sender ID</option>
                                    {smsSenderIds.map(s => <option key={s.SenderName} value={s.SenderName}>{s.SenderName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Template</label>
                                <select className="action-select" value={sendSmsData.TemplateId} onChange={(e) => {
                                    const t = smsTemplates.find(tem => (tem.DLT_TemplateID || tem.TemplateID) == e.target.value);
                                    setSendSmsData({ ...sendSmsData, TemplateId: e.target.value, Message: t ? (t.TemplateText || t.Content) : '' });
                                }}>
                                    <option value="">Select Template</option>
                                    {smsTemplates.map(t => <option key={t.DLT_TemplateID || t.TemplateID} value={t.DLT_TemplateID || t.TemplateID}>{t.TemplateName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Message</label>
                                <textarea className="action-textarea" value={sendSmsData.Message} onChange={(e) => setSendSmsData({ ...sendSmsData, Message: e.target.value })} style={{ height: '120px' }}></textarea>
                            </div>
                            <div className="form-footer">
                                <label className="checkbox-label" style={{ marginRight: 'auto' }}>
                                    <input type="checkbox" checked={sendSmsData.IsUnicode} onChange={(e) => setSendSmsData({ ...sendSmsData, IsUnicode: e.target.checked })} /> Unicode
                                </label>
                                <button className="btn-reset" onClick={() => setSendSmsData({ ...sendSmsData, Message: '', TemplateId: '', SelectedNumber: '' })}>Reset</button>
                                <button className="btn-save" onClick={handleSendSms} disabled={isSubmitting}>Send SMS</button>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'mail' && (
                        <>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>Choose Emails</label>
                                <div className="contact-selection-list">
                                    {leadContactDetails.emails.length > 0 ? (
                                        leadContactDetails.emails.map((email, i) => (
                                            <label key={i} className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="selectedEmail"
                                                    checked={sendMailData.SelectedEmail === email}
                                                    onChange={() => setSendMailData({ ...sendMailData, SelectedEmail: email })}
                                                />
                                                {email}
                                            </label>
                                        ))
                                    ) : (
                                        <div className="no-contacts-msg">No emails found for this lead</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <label className="input-label">Email Account *</label>
                                <select className="action-select" value={sendMailData.Account} onChange={(e) => { setSendMailData({ ...sendMailData, Account: e.target.value }); }}>
                                    <option value="">Select Account</option>
                                    {mailAccounts.map(acc => <option key={acc.Code || acc.AccountId} value={acc.Code || acc.AccountId}>{acc.Text || acc.AccountName}</option>)}
                                </select>
                            </div>

                            <div className="form-row">
                                <label className="input-label">From Email *</label>
                                <select className="action-select" value={sendMailData.From} onChange={(e) => setSendMailData({ ...sendMailData, From: e.target.value })}>
                                    <option value="">--Select From MailId--</option>
                                    {fromMailOptions.map(opt => <option key={opt.Value || opt.Code} value={opt.Value || opt.Code}>{opt.Text}</option>)}
                                </select>
                            </div>

                            <div className="form-row">
                                <label className="input-label">Reply To *</label>
                                <select className="action-select" value={sendMailData.ReplyTo} onChange={(e) => setSendMailData({ ...sendMailData, ReplyTo: e.target.value })}>
                                    <option value="">--Select Reply To--</option>
                                    {fromMailOptions.map(opt => <option key={opt.Value || opt.Code} value={opt.Value || opt.Code}>{opt.Text}</option>)}
                                </select>
                            </div>

                            <div className="mail-mode-selector">
                                <label className="radio-label">
                                    <input type="radio" checked={sendMailData.MailMode === 'template'} onChange={() => setSendMailData({ ...sendMailData, MailMode: 'template' })} /> Select Template
                                </label>
                                <label className="radio-label" style={{ marginLeft: '20px' }}>
                                    <input type="radio" checked={sendMailData.MailMode === 'compose'} onChange={() => setSendMailData({ ...sendMailData, MailMode: 'compose' })} /> Compose Mail
                                </label>
                            </div>

                            <div className="form-row">
                                <label className="input-label">Subject *</label>
                                <input type="text" className="action-input" value={sendMailData.Subject} onChange={(e) => setSendMailData({ ...sendMailData, Subject: e.target.value })} placeholder="Subject" />
                            </div>

                            {sendMailData.MailMode === 'template' ? (
                                <div className="form-row">
                                    <label className="input-label">Template *</label>
                                    <select className="action-select" value={sendMailData.TemplateId} onChange={(e) => {
                                        const t = mailTemplates.find(tem => (tem.Id || tem.Code || tem.MailTemplateId) == e.target.value);
                                        setSendMailData({ ...sendMailData, TemplateId: e.target.value, Body: t ? (t.Text || t.Content || t.MailTemplateContent) : '', Subject: t ? (t.Text || t.TemplateName || t.MailTemplateName) : sendMailData.Subject });
                                    }}>
                                        <option value="">Please select mail template</option>
                                        {mailTemplates.map(t => <option key={t.Id || t.Code || t.MailTemplateId} value={t.Id || t.Code || t.MailTemplateId}>{t.Text || t.TemplateName || t.MailTemplateName}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div className="form-row">
                                    <label className="input-label">Body *</label>
                                    <textarea className="action-textarea" value={sendMailData.Body} onChange={(e) => setSendMailData({ ...sendMailData, Body: e.target.value })} style={{ height: '180px' }} placeholder="Enter mail body..."></textarea>
                                </div>
                            )}

                            <div className="form-footer">
                                <button className="btn-reset" onClick={() => setSendMailData({ ...sendMailData, Subject: '', Body: '', TemplateId: '', SelectedEmail: '' })}>Reset</button>
                                <button className="btn-save" style={{ background: '#22c55e' }} onClick={handleSendMail} disabled={isSubmitting}>Save</button>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'voice' && (
                        <>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>Choose Mobile Numbers</label>
                                <div className="contact-selection-list">
                                    {leadContactDetails.mobileNumbers.length > 0 ? (
                                        leadContactDetails.mobileNumbers.map((num, i) => (
                                            <label key={i} className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="selectedVoiceNumber"
                                                    checked={sendVoiceData.SelectedNumber === num}
                                                    onChange={() => setSendVoiceData({ ...sendVoiceData, SelectedNumber: num })}
                                                />
                                                {num}
                                            </label>
                                        ))
                                    ) : (
                                        <div className="no-contacts-msg">No numbers found for this lead</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <label className="input-label">DNI Number</label>
                                <select className="action-select" value={sendVoiceData.DnidNo} onChange={(e) => setSendVoiceData({ ...sendVoiceData, DnidNo: e.target.value })}>
                                    <option value="">Select DNI</option>
                                    {dniNumbers.map(d => <option key={d.Code || (d.Id !== undefined ? d.Id : '')} value={d.Code || (d.Id !== undefined ? d.Id : '')}>{d.Code}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">App Flow</label>
                                <select className="action-select" value={sendVoiceData.AppFlow} onChange={(e) => setSendVoiceData({ ...sendVoiceData, AppFlow: e.target.value })}>
                                    <option value="">Select App Flow</option>
                                    {appFlows.map(f => {
                                        const val = f.Id !== undefined && f.Id !== null ? f.Id : f.Code;
                                        return <option key={val} value={val}>{f.Text || f.Name}</option>;
                                    })}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Route</label>
                                <div className="sms-type-tabs">
                                    <button className={sendVoiceData.Route === 'promo' ? 'active' : ''} onClick={() => setSendVoiceData({ ...sendVoiceData, Route: 'promo' })}>Promotional</button>
                                    <button className={sendVoiceData.Route === 'trans' ? 'active' : ''} onClick={() => setSendVoiceData({ ...sendVoiceData, Route: 'trans' })}>Transactional</button>
                                </div>
                            </div>
                            <div className="form-footer">
                                <button className="btn-reset" onClick={() => setSendVoiceData({ ...sendVoiceData, DnidNo: '', AppFlow: '', SelectedNumber: '' })}>Reset</button>
                                <button className="btn-save" style={{ background: '#22c55e' }} onClick={handleSendVoice} disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save'}</button>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'task' && (
                        <>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Title</label>
                                <input type="text" className="action-input" value={addTaskData.Title} onChange={(e) => setAddTaskData({ ...addTaskData, Title: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <label className="input-label">Description</label>
                                <textarea className="action-textarea" value={addTaskData.Description} onChange={(e) => setAddTaskData({ ...addTaskData, Description: e.target.value })} placeholder="Start Typing the details about the task."></textarea>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Remarks</label>
                                <textarea className="action-textarea" value={addTaskData.Remarks} onChange={(e) => setAddTaskData({ ...addTaskData, Remarks: e.target.value })} placeholder="about the task..."></textarea>
                                <div className="char-count" style={{ fontSize: '10px', color: '#888', textAlign: 'left', marginTop: '2px' }}>2000 characters left</div>
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Sales Activity Type</label>
                                <select className="action-select" value={addTaskData.SalesActivityType} onChange={(e) => setAddTaskData({ ...addTaskData, SalesActivityType: e.target.value })}>
                                    <option value="">Select Activity Type</option>
                                    {salesActivityOptions.map(o => <option key={o.TasKId || o.TaskId || o.Id} value={o.TasKId || o.TaskId || o.Id}>{o.TasKName || o.TaskName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <div className="form-group flex-1">
                                    <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Due Date and Time</label>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <input type="date" className="action-input" style={{ flex: 1 }} value={addTaskData.DueDate} onChange={(e) => setAddTaskData({ ...addTaskData, DueDate: e.target.value })} />
                                        <input type="time" className="action-input" style={{ flex: 1 }} value={addTaskData.DueTime} onChange={(e) => setAddTaskData({ ...addTaskData, DueTime: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Owner</label>
                                <select className="action-select" value={addTaskData.Owner} onChange={(e) => setAddTaskData({ ...addTaskData, Owner: e.target.value })}>
                                    {userList.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Collaborators</label>
                                <select className="action-select" value={addTaskData.Collaborators} onChange={(e) => setAddTaskData({ ...addTaskData, Collaborators: e.target.value })}>
                                    <option value="">Select Collaborator</option>
                                    {collaboratorOptions.map(u => (
                                        <option key={u.Id || u.Code} value={u.Id || u.Code}>
                                            {u.Text || u.Name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-footer" style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                <button className="btn-reset" style={{ marginRight: 'auto' }} onClick={() => setAddTaskData({ ...addTaskData, Title: '', Description: '', Remarks: '', SalesActivityType: '', DueDate: '', DueTime: '', Owner: userId, Collaborators: '' })}>Reset</button>
                                <button className="btn-save" style={{ background: '#22c55e', color: 'white', padding: '10px 30px', fontWeight: '600', borderRadius: '4px' }} onClick={() => handleSaveTask(false)} disabled={isSubmitting}>SAVE</button>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'appointment' && (
                        <>
                            <div className="appointment-form-luxury">
                                <div className="form-row-luxury">
                                    <div className="form-group-luxury full">
                                        <label className="luxury-label">Physical Appointment Type</label>
                                        <select
                                            name="ActivityType"
                                            className="luxury-select"
                                            value={appointmentData.ActivityType}
                                            onChange={handleAppointmentChange}
                                        >
                                            {physicalAppointmentTypes.map(item => (
                                                <option key={item.Id || item.Code} value={item.Id || item.Code}>
                                                    {item.TaskName || item.Text || item.Name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row-luxury">
                                    <div className="form-group-luxury full">
                                        <label className="luxury-label">Physical Appointment title</label>
                                        <input
                                            type="text"
                                            name="Title"
                                            className="luxury-input"
                                            value={appointmentData.Title}
                                            onChange={handleAppointmentChange}
                                            placeholder="Enter Physical Appointment title"
                                        />
                                    </div>
                                </div>

                                <div className="form-row-luxury">
                                    <div className="form-group-luxury full">
                                        <label className="luxury-label">Description*</label>
                                        <textarea
                                            name="Description"
                                            className="luxury-textarea"
                                            style={{ minHeight: '80px' }}
                                            value={appointmentData.Description}
                                            onChange={handleAppointmentChange}
                                            placeholder="Enter Description*"
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="form-row-luxury dual">
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">From Date and Time *</label>
                                        <div className="luxury-datetime-picker" onClick={(e) => e.currentTarget.querySelector('input[type="datetime-local"]').showPicker()}>
                                            <Calendar size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                                            <span className="luxury-datetime-display">
                                                {appointmentData.StartDate && appointmentData.StartTime
                                                    ? (() => {
                                                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                        const d = new Date(`${appointmentData.StartDate}T${appointmentData.StartTime}`);
                                                        return isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                                    })()
                                                    : 'Select date & time'}
                                            </span>
                                            <input
                                                type="datetime-local"
                                                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                                                value={appointmentData.StartDate && appointmentData.StartTime ? `${appointmentData.StartDate}T${appointmentData.StartTime}` : ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val) {
                                                        setAppointmentData(prev => ({ ...prev, StartDate: val.split('T')[0], StartTime: val.split('T')[1] }));
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Due Date and Time *</label>
                                        <div className="luxury-datetime-picker" onClick={(e) => e.currentTarget.querySelector('input[type="datetime-local"]').showPicker()}>
                                            <Calendar size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                                            <span className="luxury-datetime-display">
                                                {appointmentData.EndDate && appointmentData.EndTime
                                                    ? (() => {
                                                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                        const d = new Date(`${appointmentData.EndDate}T${appointmentData.EndTime}`);
                                                        return isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                                    })()
                                                    : 'Select date & time'}
                                            </span>
                                            <input
                                                type="datetime-local"
                                                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                                                value={appointmentData.EndDate && appointmentData.EndTime ? `${appointmentData.EndDate}T${appointmentData.EndTime}` : ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val) {
                                                        setAppointmentData(prev => ({ ...prev, EndDate: val.split('T')[0], EndTime: val.split('T')[1] }));
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row-luxury dual">
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Task Associated</label>
                                        <select name="EntityType" className="luxury-select" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                                            <option value="lead">Lead</option>
                                            <option value="enquiry">Enquiry</option>
                                            <option value="site">Site</option>
                                        </select>
                                    </div>
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Search Related To *</label>
                                        <div className="luxury-search-wrapper" style={{ position: 'relative' }}>
                                            <span className="luxury-search-icon" style={{
                                                position: 'absolute',
                                                left: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 1,
                                                color: '#94a3b8'
                                            }}>🔍</span>
                                            <input
                                                type="text"
                                                className="luxury-input"
                                                placeholder="Enter Search Related To *"
                                                value={entityName}
                                                onChange={(e) => {
                                                    setEntityName(e.target.value);
                                                    fetchRelatedSuggestions(e.target.value, entityType);
                                                }}
                                                onFocus={() => setShowRelatedDropdown(true)}
                                            />
                                            {showRelatedDropdown && relatedSuggestions.length > 0 && (
                                                <div className="luxury-suggestions-dropdown">
                                                    {relatedSuggestions.map((s, idx) => (
                                                        <div key={idx} className="luxury-suggestion-item" onClick={() => {
                                                            setEntityName(s.Name || s.Text || '');
                                                            setEntityId(s.Id || s.Code || '');
                                                            setShowRelatedDropdown(false);
                                                            fetchLeadContactDetails(s.Id || s.Code);
                                                        }}>
                                                            <div className="suggestion-avatar-wrapper">
                                                                <div className="suggestion-avatar">
                                                                    {renderAvatar ? renderAvatar(s.Name || s.Text, s.Image, 'sm', '50%') : null}
                                                                </div>
                                                            </div>
                                                            <div className="suggestion-main-info">
                                                                <div className="s-name">{s.Name || s.Text}</div>
                                                                <div className="s-meta-row">
                                                                    {s.Mobile && <span className="s-meta-item"><PhoneCall size={12} /> {s.Mobile}</span>}
                                                                    {s.City && <span className="s-meta-item"><MapPin size={12} /> {s.City}</span>}
                                                                    {(!s.Mobile && !s.City) && <span className="s-meta-item">ID: {s.Id || s.Code}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row-luxury dual">
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Assigned Field Executive</label>
                                        <select name="FieldExecutive" className="luxury-select" value={appointmentData.FieldExecutive || userId} onChange={handleAppointmentChange}>
                                            {userList.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Where *</label>
                                        <input
                                            type="text"
                                            name="Where"
                                            ref={whereInputRef}
                                            className="luxury-input"
                                            value={appointmentData.Where}
                                            onChange={handleAppointmentChange}
                                            placeholder="Enter Where *"
                                        />
                                    </div>
                                </div>

                                <div className="form-row-luxury">
                                    <div className="form-group-luxury full">
                                        <label className="luxury-label">Complete Address*</label>
                                        <textarea
                                            name="CompleteAddress"
                                            className="luxury-textarea"
                                            style={{ minHeight: '60px' }}
                                            value={appointmentData.CompleteAddress || ''}
                                            onChange={handleAppointmentChange}
                                            placeholder="Enter Complete Address*"
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="form-row-luxury dual">
                                    <div className="form-group-luxury">
                                        <label className="luxury-label">Owner</label>
                                        <select name="Owner" className="luxury-select" value={appointmentData.Owner} onChange={handleAppointmentChange}>
                                            {userList.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row-luxury">
                                    <div className="form-group-luxury full">
                                        <label className="luxury-label">Collaborators</label>
                                        <div style={{ position: 'relative' }}>
                                            <div
                                                onClick={() => setShowCollaboratorDropdown(prev => !prev)}
                                                className="luxury-multiselect-trigger"
                                            >
                                                <div className="luxury-multiselect-tags">
                                                    {Array.isArray(appointmentData.Collaborators) && appointmentData.Collaborators.length > 0 ? (
                                                        appointmentData.Collaborators.map(val => {
                                                            const opt = collaboratorOptions.find(o => String(o.Id || o.Code) === String(val));
                                                            return (
                                                                <span key={val} className="luxury-collab-tag">
                                                                    {opt?.Text || opt?.Name || val}
                                                                    <X size={12} style={{ cursor: 'pointer', marginLeft: '4px' }} onClick={(e) => { e.stopPropagation(); setAppointmentData(prev => ({ ...prev, Collaborators: prev.Collaborators.filter(c => c !== val) })); }} />
                                                                </span>
                                                            );
                                                        })
                                                    ) : (
                                                        <span style={{ color: '#94a3b8' }}>Select Collaborators</span>
                                                    )}
                                                </div>
                                                <svg style={{ width: '16px', height: '16px', color: '#94a3b8', flexShrink: 0, transition: 'transform 0.2s', transform: showCollaboratorDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </div>
                                            {showCollaboratorDropdown && (
                                                <div className="luxury-collab-dropdown">
                                                    <div className="luxury-collab-search-wrap">
                                                        <input
                                                            type="text"
                                                            className="luxury-collab-search"
                                                            placeholder="Search..."
                                                            value={collaboratorSearch}
                                                            onChange={(e) => setCollaboratorSearch(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="luxury-collab-options">
                                                        {collaboratorOptions
                                                            .filter(u => (u.Text || u.Name || '').toLowerCase().includes(collaboratorSearch.toLowerCase()))
                                                            .map((u, idx) => {
                                                                const val = String(u.Id || u.Code);
                                                                const isChecked = Array.isArray(appointmentData.Collaborators) && appointmentData.Collaborators.includes(val);
                                                                return (
                                                                    <label key={idx} className="luxury-collab-option">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isChecked}
                                                                            onChange={() => {
                                                                                setAppointmentData(prev => ({
                                                                                    ...prev,
                                                                                    Collaborators: isChecked
                                                                                        ? prev.Collaborators.filter(c => c !== val)
                                                                                        : [...(prev.Collaborators || []), val]
                                                                                }));
                                                                            }}
                                                                            className="luxury-collab-checkbox"
                                                                        />
                                                                        <span>{u.Text || u.Name}</span>
                                                                    </label>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-footer-luxury">
                                    <button className="luxury-btn-cancel" onClick={onBack}>Cancel</button>
                                    <button
                                        className="luxury-btn-save"
                                        onClick={() => handleSaveTask(true)}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {task?.actionType === 'deal' && (
                        <>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>Deal Value *</label>
                                <input type="number" className="action-input" value={dealData.Amount} onChange={(e) => setDealData({ ...dealData, Amount: e.target.value })} placeholder="Enter amount" />
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>Pipeline *</label>
                                <select className="action-select" value={dealData.PipelineId} onChange={(e) => { setDealData({ ...dealData, PipelineId: e.target.value }); fetchDealStages(e.target.value); }}>
                                    <option value="">Select Pipeline</option>
                                    {dealPipelines.map(p => <option key={p.Code} value={p.Code}>{p.Text}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Stage</label>
                                <select className="action-select" value={dealData.PipelineStage} onChange={(e) => setDealData({ ...dealData, PipelineStage: e.target.value })}>
                                    <option value="">Select Stage</option>
                                    {dealStages.map(s => <option key={s.Code} value={s.Code}>{s.Text}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label">Probability (%)</label>
                                <input type="number" className="action-input" value={dealData.Probability} onChange={(e) => setDealData({ ...dealData, Probability: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Owner</label>
                                <select className="action-select" value={dealData.DealOwner} onChange={(e) => setDealData({ ...dealData, DealOwner: e.target.value })}>
                                    {userList.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <label className="input-label" style={{ color: '#9a3412', fontWeight: '600' }}>* Estimated Closer Date</label>
                                <input type="date" className="action-input" value={dealData.ClosureDate} onChange={(e) => setDealData({ ...dealData, ClosureDate: e.target.value })} />
                            </div>
                            <div className="form-footer" style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                <button className="btn-reset" style={{ marginRight: 'auto' }} onClick={() => setDealData({ Amount: '', PipelineId: '', PipelineStage: '', Probability: '', DealOwner: userId, ClosureDate: new Date().toISOString().split('T')[0] })}>Reset</button>
                                <button className="btn-save" style={{ background: '#22c55e', color: 'white', padding: '10px 30px', fontWeight: '600', borderRadius: '4px' }} onClick={handleSaveDeal} disabled={isSubmitting}>SAVE</button>
                            </div>
                        </>
                    )}

                    {(task?.actionType === 'webform' || task?.actionType === 'fill-webform') && (
                        <div className="webform-horizontal-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                            <label className="input-label" style={{ whiteSpace: 'nowrap', marginBottom: 0, color: '#475569', fontWeight: '600' }}>*Webform Name</label>
                            <select
                                className="action-select"
                                style={{ flex: 1 }}
                                value={selectedWebform}
                                onChange={(e) => setSelectedWebform(e.target.value)}
                            >
                                <option value="">--Select Webform--</option>
                                {webforms.map(w => (
                                    <option key={w.Id || w.Code || w.WebFormId} value={w.Id || w.Code || w.WebFormId}>
                                        {w.Text || w.Name || w.WebFormName}
                                    </option>
                                ))}
                            </select>
                            <span
                                className="btn-reset"
                                style={{ cursor: 'pointer', color: '#64748b', fontSize: '14px' }}
                                onClick={() => setSelectedWebform('')}
                            >
                                Reset
                            </span>
                            <button
                                className="btn-save"
                                style={{
                                    background: 'white',
                                    color: '#22c55e',
                                    border: '1px solid #22c55e',
                                    padding: '6px 20px',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    height: '38px'
                                }}
                                onClick={handleFillWebform}
                            >
                                SAVE
                            </button>
                        </div>
                    )}

                    {/* {(task?.actionType === 'notes' || task?.actionType === 'add-notes') && (
                        <>
                            <div className="form-row">
                                <label className="input-label">Note Description</label>
                                <textarea className="action-textarea" style={{ minHeight: '150px' }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter your notes here..."></textarea>
                            </div>
                            <div className="form-footer">
                                <button className="btn-save" onClick={handleAddNotes} disabled={isSubmitting}>Add Note</button>
                            </div>
                        </>
                    )} */}

                    {task?.actionType === 'upload' && (
                        <>
                            <div className="form-row">
                                <label className="input-label">Title</label>
                                <input type="text" className="action-input" value={uploadData.Title} onChange={(e) => setUploadData({ ...uploadData, Title: e.target.value })} placeholder="Enter document title" />
                            </div>
                            <div className="form-row">
                                <label className="input-label">Choose File</label>
                                <input type="file" className="action-input" onChange={(e) => setUploadData({ ...uploadData, File: e.target.files[0], FileName: e.target.files[0]?.name })} />
                            </div>
                            <div className="form-footer">
                                <button className="btn-save" onClick={handleUpload} disabled={isSubmitting}>Upload Document</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <Alert 
                show={alertConfig.show}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
                onConfirm={() => {
                    setAlertConfig(prev => ({ ...prev, show: false }));
                    if (alertConfig.onConfirm) alertConfig.onConfirm();
                }}
            />
        </div>
    );
};


export default ActionOperations;
